import {
  createContext,
  useCallback,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { isEqual, omit } from "lodash";
import {
  StringParam,
  useQueryParam,
  useQueryParams,
  withDefault,
} from "use-query-params";
import { useEffectOnce } from "usehooks-ts";

import { LoadingSlide } from "@components/title-slide-view";
import { useUser } from "@components/user-context";
import {
  AccountingModeParam,
  ActiveTabParam,
  AttributionWindowParam,
  BreakdownParam,
  CompareDateRangeParam,
  DateRangeParam,
  FlyoutTabParam,
  GranularityParam,
  MetricsFilterParam,
  SalesSortingOrderParam,
  SalesSortingParam,
  SearchQueryParam,
  StatusesParam,
  TimeGranularityParam,
  type SalesSorting,
} from "@features/sales-v3/context";
import {
  ChartStateJsonParam,
  type ChartState,
} from "@features/sales-v3/hooks/use-chart-state";
import { type SavedView } from "@features/sales-v3/hooks/use-saved-views-queries";
import {
  TableStateJsonParam,
  type TableState,
} from "@features/sales-v3/hooks/use-table-state";
import { type TabId } from "@features/sales-v3/tabs";
import {
  type FlyoutTabId,
  type TopLevelBreakdown,
} from "@features/sales-v3/types";
import { deriveComparisonDateRange } from "@features/sales-v3/utils/compare-dates-helpers";
import {
  type CreativesSortingOrder,
  type CreativesStatusEnum,
  type SalesTableReportV3MetricsFilterInput,
} from "@north-beam/nb-client-side";
import {
  todayLocalAsISODate,
  type CompareDateRange,
  type DateRangeChoiceV3,
  type FixedDateInterval,
  type IMCAttributionWindowDays,
  type INBAccountingMode,
  type ITimeGranularityV2,
} from "@north-beam/nb-common";
import { parseDateRangeArgument } from "@utils/index";

import { useSalesSavedViews } from "../sales-saved-views-provider";

import { DEFAULT_PAGE_CONFIGURATION } from "./constants";
import { type SalesV3NextContextDescriptor } from "./types";

// export const isDefaultPageConfiguration = (pageConfig: PageConfiguration) =>
//   isEqual(pageConfig, DEFAULT_PAGE_CONFIGURATION);

export const SalesPageV3NextContext = createContext<
  SalesV3NextContextDescriptor | undefined
>(undefined);

export const SalesPageV3NextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { user } = useUser();
  const { savedViews } = useSalesSavedViews();
  //   const { resetAllStates } = useResetTabStates();
  //   const logger = useLogger({
  //     container: "breakdowns-management",
  //   });

  const [selectedSavedViewId, setSelectedSavedViewId] = useQueryParam(
    "savedViewId",
    StringParam,
    { updateType: "pushIn" },
  );

  const [pageConfiguration, setPageConfiguration] = useQueryParams(
    {
      attributionModel: withDefault(
        StringParam,
        DEFAULT_PAGE_CONFIGURATION.attributionModel,
      ),
      attributionWindow: withDefault(
        AttributionWindowParam,
        DEFAULT_PAGE_CONFIGURATION.attributionWindow,
      ),
      accountingMode: withDefault(
        AccountingModeParam,
        DEFAULT_PAGE_CONFIGURATION.accountingMode,
      ),
      timeGranularity: withDefault(
        TimeGranularityParam,
        DEFAULT_PAGE_CONFIGURATION.timeGranularity,
      ),
      dateRange: withDefault(
        DateRangeParam,
        DEFAULT_PAGE_CONFIGURATION.dateRange,
      ),
      compareDateRange: withDefault(
        CompareDateRangeParam,
        DEFAULT_PAGE_CONFIGURATION.compareDateRange,
      ),
      granularity: withDefault(
        GranularityParam,
        DEFAULT_PAGE_CONFIGURATION.granularity,
      ),
      breakdowns: withDefault(
        BreakdownParam,
        DEFAULT_PAGE_CONFIGURATION.breakdowns,
      ),
      filters: withDefault(BreakdownParam, DEFAULT_PAGE_CONFIGURATION.filters),
      statuses: withDefault(StatusesParam, null),
      sorting: withDefault(
        SalesSortingParam,
        DEFAULT_PAGE_CONFIGURATION.sorting,
      ),
      sortingOrder: withDefault(
        SalesSortingOrderParam,
        DEFAULT_PAGE_CONFIGURATION.sortingOrder,
      ),
      metricFilters: withDefault(
        MetricsFilterParam,
        DEFAULT_PAGE_CONFIGURATION.metricFilters,
      ),
      searchQuery: withDefault(
        SearchQueryParam,
        DEFAULT_PAGE_CONFIGURATION.searchQuery,
      ),
      flyoutId: withDefault(StringParam, DEFAULT_PAGE_CONFIGURATION.flyoutId),
      flyoutTab: withDefault(
        FlyoutTabParam,
        DEFAULT_PAGE_CONFIGURATION.flyoutTab,
      ),
      activeTab: withDefault(ActiveTabParam, "breakdown"),
    },
    // batching breaks dnd kit sorting
    {
      enableBatching: false,
      skipUpdateWhenNoChange: true,
      updateType: "pushIn",
    },
  );

  const [chartState, setChartState] = useQueryParam<ChartState>(
    "chartState",
    ChartStateJsonParam,
    { updateType: "pushIn" },
  );

  const [tableState, setTableState] = useQueryParam<TableState>(
    "tableState",
    TableStateJsonParam,
  );

  //   const { value: breakdownConfigs } = useSalesBreakdownConfigs();
  //   const [wholeBreakdowns, setWholeBreakdowns] = useState<TopLevelBreakdown[]>(
  //     [],
  //   );
  //   useEffect(() => {
  //     const matchedWholeBreakdowns = pageConfiguration.breakdowns
  //       .map(({ key }) => {
  //         const matchedBreakdown = breakdownConfigs.find(
  //           (breakdown) => breakdown.key === key,
  //         );
  //         if (matchedBreakdown) {
  //           return {
  //             key: matchedBreakdown.key,
  //             values: matchedBreakdown.choices.map(({ value }) => value),
  //           };
  //         }

  //         return undefined;
  //       })
  //       .filter(Boolean) as TopLevelBreakdown[];

  //     setWholeBreakdowns(matchedWholeBreakdowns);
  //   }, [pageConfiguration.breakdowns, breakdownConfigs]);

  const [expandedTableRowIds, setExpandedTableRowIds] = useState<string[]>([]);
  const [isInitialSavedViewSelected, setIsInitialSavedViewSelected] =
    useState(false);

  const setAttributionModel = useCallback(
    (value: string) => {
      setPageConfiguration({ attributionModel: value });
    },
    [setPageConfiguration],
  );

  const setAttributionWindow = useCallback(
    (value: IMCAttributionWindowDays) => {
      setPageConfiguration({ attributionWindow: value });
    },
    [setPageConfiguration],
  );

  const setAccountingMode = useCallback(
    (value: INBAccountingMode) => {
      setPageConfiguration({ accountingMode: value });
    },
    [setPageConfiguration],
  );

  const setTimeGranularity = useCallback(
    (value: ITimeGranularityV2) => {
      setPageConfiguration({ timeGranularity: value });
    },
    [setPageConfiguration],
  );

  const setDateRange = useCallback(
    (value: FixedDateInterval | DateRangeChoiceV3) => {
      setPageConfiguration({ dateRange: value });
    },
    [setPageConfiguration],
  );

  const setCompareDateRange = useCallback(
    (value: CompareDateRange | null) => {
      setPageConfiguration({ compareDateRange: value });
    },
    [setPageConfiguration],
  );

  const setBreakdowns = useCallback(
    (values: TopLevelBreakdown[]) => {
      const breakdownKeysOnly = values.map(({ key }) => ({
        key,
        values: [],
      }));
      setPageConfiguration({ breakdowns: breakdownKeysOnly });
      // logger.event.salesPageUpdateBreakdowns("change", {
      //   eventDetails: { breakdowns: values },
      // });
      // resetAllStates();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setPageConfiguration],
  );

  const setFilters = useCallback(
    (values: TopLevelBreakdown[]) => {
      setPageConfiguration({ filters: values });
      // resetAllStates();
    },
    [setPageConfiguration],
  );

  const setStatuses = useCallback(
    (value: CreativesStatusEnum[] | null) => {
      setPageConfiguration({ statuses: value });
    },
    [setPageConfiguration],
  );

  const setSorting = useCallback(
    (value: SalesSorting | null) => {
      setPageConfiguration({ sorting: value });
    },
    [setPageConfiguration],
  );

  const setSortingOrder = useCallback(
    (value: CreativesSortingOrder | null) => {
      setPageConfiguration({ sortingOrder: value });
    },
    [setPageConfiguration],
  );

  const setMetricFilters = useCallback(
    (metricFilters: SalesTableReportV3MetricsFilterInput) => {
      setPageConfiguration({ metricFilters });
    },
    [setPageConfiguration],
  );

  const setActiveTab = useCallback(
    (tab: TabId) => {
      setPageConfiguration({
        activeTab: tab,
        granularity: tab === "breakdown" ? "campaign" : tab,
      });
    },
    [setPageConfiguration],
  );

  const setFlyoutId = useCallback(
    (value: string | null) => {
      setPageConfiguration({ flyoutId: value });
    },
    [setPageConfiguration],
  );

  const setFlyoutTab = useCallback(
    (value: FlyoutTabId) => {
      setPageConfiguration({ flyoutTab: value });
    },
    [setPageConfiguration],
  );

  const selectSavedView = useCallback(
    (savedView: SavedView) => {
      setSelectedSavedViewId(String(savedView.id));
      setPageConfiguration(savedView.config.pageConfiguration);
      setChartState(savedView.config.chartState);
      setTableState({
        ...savedView.config.tableState,
        columnConfigs: (savedView.config.tableState?.columnConfigs ?? []).map(
          (i) => omit(i, "name"),
        ),
      });
    },
    [
      savedViews,
      setSelectedSavedViewId,
      setPageConfiguration,
      setChartState,
      setTableState,
    ],
  );

  const dateRangeInterval = useMemo(() => {
    return parseDateRangeArgument(
      pageConfiguration.dateRange,
      todayLocalAsISODate(),
    );
  }, [pageConfiguration.dateRange]);

  const compareDateRangeInterval = useMemo(() => {
    return pageConfiguration.compareDateRange
      ? deriveComparisonDateRange(
          dateRangeInterval,
          pageConfiguration.compareDateRange,
          user.timezone,
          pageConfiguration.timeGranularity,
        )
      : null;
  }, [
    pageConfiguration.compareDateRange,
    pageConfiguration.timeGranularity,
    dateRangeInterval,
    user.timezone,
  ]);

  const normalizedAttributionWindow = useMemo(() => {
    /* Note: one of the limitations of hourly data processing is that we cannot
       process/store efficiently all historical data on an hourly grain. so we run
       attribution on the last 30 days worth of data only (no data prior to 30d),
       and report the last 7. */
    if (pageConfiguration.timeGranularity === "hourly") {
      return "1";
    }

    if (pageConfiguration.accountingMode === "cash") {
      return "infinity";
    }

    if (
      pageConfiguration.accountingMode === "accrual" &&
      pageConfiguration.attributionWindow === "infinity"
    ) {
      return "1";
    }

    return pageConfiguration.attributionWindow;
  }, [
    pageConfiguration.attributionWindow,
    pageConfiguration.accountingMode,
    pageConfiguration.timeGranularity,
  ]);

  const selectedSavedView = useMemo(() => {
    return (
      savedViews.find((e) => e.id === Number(selectedSavedViewId)) ||
      savedViews[0]
    );
  }, [savedViews, selectedSavedViewId]);

  useEffectOnce(() => {
    const savedView = savedViews.find(
      (e) => e.id === Number(selectedSavedViewId),
    );

    if (!savedView && savedViews[0]) {
      selectSavedView(savedViews[0]);
    }

    setTimeout(() => {
      setIsInitialSavedViewSelected(true);
    }, 0);
  });
  // // Selects saved view when saved views are loaded
  // useEffect(() => {
  //   if (!selectedSavedView && selectedSavedViewId && !isBreakdownLoading) {
  //     const savedView = findSavedView(savedViews, selectedSavedViewId);

  //     if (savedView) {
  //       const {
  //         config: { chartState, pageConfiguration, tableState: _tableState },
  //         is_default,
  //       } = savedView;

  //       setPageConfiguration((prevPageConfiguration) => {
  //         const pageConfigurationCopy = { ...pageConfiguration };
  //         if (is_default && pageConfigurationCopy.breakdowns.length) {
  //           const templateBreakdowns = getTemplateBreakdownsForSavedView({
  //             pageConfiguration: pageConfigurationCopy,
  //             breakdownConfigs,
  //           });
  //           pageConfigurationCopy.breakdowns = templateBreakdowns;
  //         }

  //         if (
  //           isEqual(prevPageConfiguration, pageConfigurationCopy) ||
  //           isDefaultPageConfiguration(prevPageConfiguration)
  //         ) {
  //           return pageConfigurationCopy;
  //         }
  //         return prevPageConfiguration;
  //       }, "pushIn");

  //       setChartState((prevChartState) => {
  //         if (
  //           isEqual(prevChartState, chartState) ||
  //           isDefaultChartState(prevChartState)
  //         ) {
  //           return chartState;
  //         }
  //         return prevChartState;
  //       }, "pushIn");

  //       const tableState = {
  //         ..._tableState,
  //         columnConfigs: _tableState.columnConfigs.map((i) => omit(i, "name")),
  //       };

  //       setTableState((prevTableState) => {
  //         if (
  //           isEqual(prevTableState, tableState) ||
  //           isDefaultTableState(prevTableState)
  //         ) {
  //           return tableState;
  //         }
  //         return prevTableState;
  //       }, "pushIn");

  //       setSelectedSavedView((prevSavedView) => {
  //         if (prevSavedView?.id === savedView.id) {
  //           return prevSavedView;
  //         }
  //         return savedView;
  //       });
  //     }
  //   }
  // }, [
  //   isBreakdownLoading,
  //   breakdownConfigs,
  //   savedViews,
  //   selectedSavedView,
  //   selectedSavedViewId,
  //   setChartState,
  //   setPageConfiguration,
  //   setTableState,
  //   setSelectedSavedView,
  // ]);

  const isStateSaved = useMemo(() => {
    if (!selectedSavedView) {
      return false;
    }

    const savedPageConfigs = selectedSavedView.config.pageConfiguration;
    const isPageConfigEqual = isEqual(
      {
        ...pageConfiguration,
        breakdowns: pageConfiguration.breakdowns.map((i) => i.key),
      },
      {
        ...savedPageConfigs,
        breakdowns: savedPageConfigs.breakdowns.map((i) => i.key),
      },
    );

    const isChartStateEqual = isEqual(
      chartState.chartConfigs,
      selectedSavedView.config.chartState.chartConfigs,
    );

    const isTableStateEqual = isEqual(tableState, {
      ...selectedSavedView.config.tableState,
      columnConfigs: selectedSavedView.config.tableState.columnConfigs.map(
        (i) => omit(i, "name"),
      ),
    });

    return isPageConfigEqual && isChartStateEqual && isTableStateEqual;
  }, [selectedSavedView, pageConfiguration, chartState, tableState]);

  return (
    <SalesPageV3NextContext.Provider
      value={{
        pageConfiguration: {
          ...pageConfiguration,
          attributionWindow: normalizedAttributionWindow,
        },
        chartState,
        tableState,
        ...pageConfiguration,
        attributionWindow: normalizedAttributionWindow,
        dateRangeInterval,
        compareDateRangeInterval,
        selectedSavedView,
        isStateSaved,
        expandedTableRowIds,
        setAttributionModel,
        setAttributionWindow,
        setAccountingMode,
        setTimeGranularity,
        setDateRange,
        setCompareDateRange,
        setBreakdowns,
        setFilters,
        setStatuses,
        setSorting,
        setSortingOrder,
        setMetricFilters,
        setActiveTab,
        //         setSearchQuery: useCallback(
        //           (value: SearchQuery) => {
        //             setPageConfiguration({ searchQuery: value }, "pushIn");
        //           },
        //           [setPageConfiguration],
        //         ),
        setFlyoutId,
        setFlyoutTab,
        setExpandedTableRowIds,
        selectSavedView,
      }}
    >
      {isInitialSavedViewSelected ? (
        children
      ) : (
        <div className="h-[calc(100vh-56px)]">
          <LoadingSlide />
        </div>
      )}
    </SalesPageV3NextContext.Provider>
  );
};
